import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import LeadForm from '@/components/LeadForm';
import api from '@/utils/api';
import { MapPin, Building2, CheckCircle, Phone, MessageCircle, ChevronLeft } from 'lucide-react';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const ProjectDetail = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await api.get(`/properties/${id}`);
        setProperty(response.data);
      } catch (error) {
        console.error('Error fetching property:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [id]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <Header />
        <p className="text-white/60">Loading project details...</p>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <Header />
        <p className="text-white/60">Project not found</p>
      </div>
    );
  }

  const defaultLat = property.latitude || 19.0760;
  const defaultLng = property.longitude || 72.8777;

  return (
    <div className="min-h-screen bg-dark" data-testid="property-detail-page">
      <Header />

      {/* Urgency Banner */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-center py-2 text-xs md:text-sm font-bold uppercase tracking-wider">
        ⚡ Limited Units Available • Best Prices Guaranteed • Free Site Visit ⚡
      </div>

      <div className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        {/* Breadcrumb */}
        <div className="mb-8 flex items-center gap-2 text-sm text-white/60">
          <Link to="/" className="hover:text-rose-gold transition-colors">Home</Link>
          <span>/</span>
          <Link to="/projects" className="hover:text-rose-gold transition-colors">Projects</Link>
          <span>/</span>
          <span className="text-rose-gold font-semibold">{property.title}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Image Gallery */}
            <div>
              <div className="relative h-[500px] overflow-hidden rounded-2xl mb-4 border-2 border-dark-lighter">
                <img
                  src={property.images[selectedImage]}
                  alt={property.title}
                  data-testid="property-main-image"
                  className="w-full h-full object-cover"
                />
                {property.status === 'Sold' && (
                  <div className="absolute top-6 left-6 bg-red-600 text-white px-6 py-3 rounded-full font-bold uppercase tracking-wider">
                    SOLD OUT
                  </div>
                )}
                <div className="absolute top-6 right-6 bg-green-500 text-white px-4 py-2 rounded-full font-bold text-sm uppercase tracking-wider">
                  Best Price Guaranteed
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                {property.images.map((image, index) => (
                  <button
                    key={index}
                    data-testid={`property-thumbnail-${index}`}
                    onClick={() => setSelectedImage(index)}
                    className={`h-24 overflow-hidden rounded-xl border-2 transition-all ${
                      selectedImage === index
                        ? 'border-rose-gold shadow-rose-glow'
                        : 'border-dark-lighter hover:border-rose-gold/50'
                    }`}
                  >
                    <img src={image} alt={`View ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            {/* Property Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div>
                <Link
                  to={`/builders/${encodeURIComponent(property.builder)}`}
                  className="inline-flex items-center gap-2 text-rose-gold font-semibold mb-4 hover:underline uppercase tracking-wider text-sm"
                >
                  <Building2 size={18} />
                  <span>{property.builder}</span>
                </Link>

                <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-4" data-testid="property-title">
                  {property.title}
                </h1>

                <div className="flex items-center gap-2 text-white/70 mb-6">
                  <MapPin size={20} className="text-rose-gold" />
                  <span data-testid="property-location" className="text-lg">{property.location}</span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-6 p-8 glass-effect rounded-2xl border border-dark-lighter">
                  <div>
                    <p className="text-white/50 text-sm mb-2 uppercase tracking-wider">Starting Price</p>
                    <p className="font-heading text-4xl font-bold text-rose-gold" data-testid="property-price">
                      {formatPrice(property.price)}
                    </p>
                  </div>
                  <div>
                    <p className="text-white/50 text-sm mb-2 uppercase tracking-wider">Area</p>
                    <p className="text-3xl font-semibold text-white" data-testid="property-area">
                      {property.area} {property.area_unit}
                    </p>
                  </div>
                  <div>
                    <p className="text-white/50 text-sm mb-2 uppercase tracking-wider">Type</p>
                    <p className="text-3xl font-semibold text-white">
                      {property.property_type}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t border-dark-lighter pt-8">
                <h2 className="font-heading text-3xl font-semibold text-white mb-6">About this Project</h2>
                <p className="text-white/70 text-lg leading-relaxed" data-testid="property-description">
                  {property.description}
                </p>
              </div>

              {/* Quick Contact CTA */}
              <div className="p-6 bg-gradient-to-r from-rose-gold/20 to-rose-dark/20 border border-rose-gold/30 rounded-2xl">
                <p className="text-white font-semibold mb-4 text-lg">Interested in this property?</p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href="tel:+917020970161" className="btn-primary inline-flex items-center justify-center gap-2 text-sm uppercase tracking-wider flex-1">
                    <Phone size={18} />
                    Call Now - Get Best Price
                  </a>
                  <a href="https://wa.me/917020970161" target="_blank" rel="noopener noreferrer" className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-full font-bold transition-all inline-flex items-center justify-center gap-2 text-sm uppercase tracking-wider flex-1">
                    <MessageCircle size={18} />
                    WhatsApp
                  </a>
                </div>
              </div>

              {/* Highlights */}
              {property.highlights && property.highlights.length > 0 && (
                <div className="border-t border-dark-lighter pt-8">
                  <h2 className="font-heading text-3xl font-semibold text-white mb-6">Key Highlights</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="property-highlights">
                    {property.highlights.map((highlight, index) => (
                      <div key={index} className="flex items-start gap-3 p-4 glass-effect rounded-xl border border-dark-lighter">
                        <CheckCircle className="text-rose-gold flex-shrink-0 mt-1" size={20} />
                        <span className="text-white/80">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Map */}
              <div className="border-t border-dark-lighter pt-8">
                <h2 className="font-heading text-3xl font-semibold text-white mb-6">Location</h2>
                <div className="h-[400px] rounded-2xl overflow-hidden border-2 border-dark-lighter" data-testid="property-map">
                  <MapContainer
                    center={[defaultLat, defaultLng]}
                    zoom={13}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    />
                    <Marker position={[defaultLat, defaultLng]}>
                      <Popup>{property.location}</Popup>
                    </Marker>
                  </MapContainer>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sticky Lead Form Sidebar */}
          <div className="lg:sticky lg:top-32 h-fit space-y-6">
            <LeadForm propertyId={property.id} propertyTitle={property.title} />
            
            <div className="p-6 glass-effect border border-dark-lighter rounded-2xl">
              <h3 className="font-heading text-xl font-semibold text-white mb-4">Why Book Through Propvanta?</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-rose-gold flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">Trusted Channel Partner</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-rose-gold flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">100% Verified Listings</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-rose-gold flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">Expert Property Guidance</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-rose-gold flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">Free Site Visits</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-rose-gold flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">Best Price Guaranteed</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProjectDetail;
